export type Phase = 'new' | 'packing' | 'ready' | 'enroute' | 'delivered' | 'cancelled';

export interface PhaseDef { key: Phase; label: string; pin: string; }

export const PHASES: PhaseDef[] = [
  { key: 'new', label: 'New', pin: '#ff6a3d' },
  { key: 'packing', label: 'Packing', pin: '#f5a623' },
  { key: 'ready', label: 'Ready', pin: '#34eec1' },
  { key: 'enroute', label: 'En route', pin: '#2f6bff' },
  { key: 'delivered', label: 'Delivered', pin: '#16b981' },
  { key: 'cancelled', label: 'Cancelled', pin: '#ff4d54' },
];

export const PHASE_BY_KEY: Record<Phase, PhaseDef> = Object.fromEntries(
  PHASES.map((p) => [p.key, p]),
) as Record<Phase, PhaseDef>;

/**
 * The columns the board actually draws.
 *
 * Cancelled is not one of them — nothing is done about a cancelled order, so a
 * column of them is just noise on a board meant to show work in flight. The
 * phase itself stays (statuses still map to it, and the order drawer still
 * labels it); it is only filtered out of the live board, in useLiveOrders.
 */
export const BOARD_PHASES: PhaseDef[] = PHASES.filter((p) => p.key !== 'cancelled');

/**
 * Phases an order never leaves.
 *
 * Two things key off this: the card timer stops (a 3-day-old delivered order
 * shouldn't glow red), and the board only keeps a recent window of them
 * (TERMINAL_WINDOW_HOURS) so history doesn't crowd out the live columns.
 */
export const TERMINAL_PHASES: Phase[] = ['delivered', 'cancelled'];

const STATUS_MAP: Record<string, Phase> = {
  placed: 'new', picking: 'packing', packed: 'ready',
  dispatched: 'enroute', tracking: 'enroute', arrived: 'enroute',
  delivered: 'delivered', cancelled: 'cancelled', returned: 'cancelled',
};

export function phaseOf(status: string | null | undefined): Phase {
  return STATUS_MAP[(status || 'placed').toLowerCase()] ?? 'new';
}

export const STAFF_ACTIONABLE: Phase[] = ['new', 'packing'];
export function isReflected(phase: Phase): boolean {
  return !STAFF_ACTIONABLE.includes(phase);
}

export const PHASE_ACTION: Partial<Record<Phase, { label: string; accent: boolean }>> = {
  new: { label: 'Accept order', accent: true },
  packing: { label: 'Mark packed', accent: false },
};

const PHASE_HINT: Record<Phase, string> = {
  new: 'Awaiting acceptance', packing: 'Bagging the order', ready: 'Staged for collection',
  enroute: 'Courier on the way', delivered: 'Completed', cancelled: 'Cancelled',
};
export function phaseHint(phase: Phase): string { return PHASE_HINT[phase]; }

const REFLECT_LABEL: Partial<Record<Phase, string>> = {
  ready: 'Awaiting courier', enroute: 'Courier en route', delivered: 'Delivered', cancelled: 'Cancelled',
};
export function reflectLabel(phase: Phase): string {
  return REFLECT_LABEL[phase] ?? PHASE_BY_KEY[phase].label;
}
